import React, { useState, useRef, PropsWithChildren } from "react";
import { useUniqueId } from "../../hooks/useUniqueId";

import { Chevron } from "../Chevron/Chevron";
import { Dropdown } from "../Dropdown/Dropdown";
import { Alignments } from "../Dropdown/hooks/usePositionAt";

import { Option } from "./Option";
import * as css from "./styles/Select";

type Props<V> = {
  label?: string;
  value: V;
  options: Option<V>[];

  containerRef?: React.RefObject<HTMLElement | null>;
  offsetTopPx?: number;

  onValueChanged: (newValue: Option<V>) => void;
};

export function Select<V>({
  label,
  containerRef,
  offsetTopPx,
  options,
  onValueChanged,
}: PropsWithChildren<Props<V>>): React.ReactElement {
  const id = `select-${useUniqueId()}`;
  const [showOptions, setShowOptions] = useState(false);
  const selectRef = useRef<HTMLLabelElement>(null);

  const toggleDropdown = (): void => {
    setShowOptions(!showOptions);
  };

  const closeDropdown = (): void => {
    setShowOptions(false);
  };

  const onOptionClick = (newOption: Option<V>): void => {
    onValueChanged(newOption);
    setShowOptions(false);
  };

  return (
    <label htmlFor={id} ref={selectRef}>
      <button
        className={css.buttonCls}
        aria-haspopup="listbox"
        aria-expanded={showOptions}
        aria-controls={id}
        onClick={toggleDropdown}
      >
        <span className={css.textCls}>{label}</span>
        <span className={css.chevronWrapperCls}>
          <Chevron
            inversed={showOptions}
            vertical
            size={8}
            lineWidth={1.5}
            color="#CCCCCC"
          />
        </span>
      </button>

      <Dropdown
        active={showOptions}
        pointAtRef={selectRef}
        offsetTopPx={offsetTopPx}
        alignment={Alignments.RIGHT}
        containerRef={containerRef}
        onClose={closeDropdown}
      >
        <ul className={css.listCls}>
          {options.map((option) => (
            <Option
              key={`${option.label}-${option.value}`}
              option={option}
              onClick={onOptionClick}
            />
          ))}
        </ul>
      </Dropdown>
    </label>
  );
}
